import { Link, useSearchParams } from "@remix-run/react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/components/ui/breadcrumb";
import { getCountryName } from "~/lib/helpers/constants";
import React from "react";
import { DEFAULT_REGION } from "~/lib/constants/generalConstants";
import { generateBreadcrumbSchema } from "~/lib/helpers/schemaHelper";
import { GetDomainName } from "~/helpers/UrlHelper";
import { titleCaseSlug } from "~/lib/helpers/StringHelper";
import { useDisplayedLocation } from "~/lib/hooks/useDisplayedLocation";
import {
  formatLocationAddressQueryLabel,
  isCountryPageSlug,
  officeTypeSlugToValue,
  parseCountryLandingSegment,
} from "~/components/blocks/Geosearch/locationGeosearch";

/** Human-readable, capitalized label for a URL path segment (matches the visible breadcrumb text). */
const formatBreadcrumbSegmentLabel = (segment: string) => {
  const decoded = decodeURIComponent(segment);
  return decoded.length <= 3
    ? decoded.toUpperCase().replace(/-/g, " ")
    : titleCaseSlug(decoded).replace(/-/g, " ");
};

const officeTypeCrumbLabel = (slug: string) =>
  slug
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const resolveBreadcrumbSegmentLabel = (
  segment: string,
  options: { isPropertyPage?: boolean; overrideLabel?: string; country?: string },
) => {
  if (options.overrideLabel) return options.overrideLabel;
  if (options.country) {
    const landing = parseCountryLandingSegment(options.country, segment);
    if (landing?.officeTypeSlug && officeTypeSlugToValue(landing.officeTypeSlug)) {
      return officeTypeCrumbLabel(landing.officeTypeSlug);
    }
  }
  if (options.isPropertyPage && isCountryPageSlug(segment)) {
    return "Office Space";
  }
  return formatBreadcrumbSegmentLabel(segment);
};

type BreadcrumbSegmentOverride = {
  slug: string;
  label?: string;
};

export default function Breadcrumbs({
  className,
  params,
  countryRequired = false,
  isPropertyPage = false,
  segments: segmentOverrides,
  useCurrentPathForLastSegment = false,
}: {
  className?: string;
  params: { country: string };
  countryRequired?: boolean;
  isPropertyPage?: boolean;
  /** When set (e.g. area pages), build crumbs from these slugs instead of the URL path. */
  segments?: BreadcrumbSegmentOverride[];
  /** Keep the last crumb on the current page URL (area id pages). */
  useCurrentPathForLastSegment?: boolean;
}) {
  const location = useDisplayedLocation();
  const [searchParams] = useSearchParams();
  const queryLabel = formatLocationAddressQueryLabel(searchParams.get("q"));
  const hasQueryCrumb = Boolean(queryLabel);
  const pathSegments = location.pathname.split("/").filter(Boolean);

  const filteredSegments =
    segmentOverrides?.map((segment) => segment.slug) ??
    pathSegments.filter((segment) => segment !== "search" && segment !== params.country);
  const homePath = params?.country === "us" ? "/" : `/${params.country}`;
  // Country-level crumb: new property pages use the `/{country}/office-space-<country>`
  // landing page; the legacy search page keeps `/{country}/search`.
  const countryName = getCountryName(params.country || DEFAULT_REGION);
  const countryPath = isPropertyPage
    ? `/${params.country}/office-space-${countryName.toLowerCase().trim().replace(/\s+/g, "-")}`
    : `/${params.country}/search`;
  // Use slice-by-index (not indexOf) so repeated slugs like /ae/dubai/dubai keep
  // unique hrefs and React keys. indexOf always returns the first "dubai", so the
  // leftover keyed fragment survives a later search (e.g. Dubai → London).
  const getSegmentPath = (index: number) => {
    const isLast = !hasQueryCrumb && index === filteredSegments.length - 1;
    if (segmentOverrides) {
      if (isLast && useCurrentPathForLastSegment) return location.pathname;
      return `/${params.country}/${filteredSegments.slice(0, index + 1).join("/")}`;
    }
    const prefixLen = pathSegments.length - filteredSegments.length;
    return `/${pathSegments.slice(0, prefixLen + index + 1).join("/")}`;
  };
  // Collapse consecutive duplicate slugs (e.g. /ae/dubai/dubai) into one crumb,
  // keeping the deeper href so the last item still represents the current page.
  const visibleSegments = filteredSegments.reduce<
    { segment: string; path: string; index: number; overrideLabel?: string }[]
  >((items, segment, index) => {
    const path = getSegmentPath(index);
    const previous = items[items.length - 1];
    if (previous && previous.segment.toLowerCase() === segment.toLowerCase()) {
      previous.path = path;
      previous.index = index;
      return items;
    }
    items.push({
      segment,
      path,
      index,
      overrideLabel: segmentOverrides?.[index]?.label,
    });
    return items;
  }, []);
  const breadcrumbData = [
    { name: "Home", path: homePath },
    ...(countryRequired ? [{ name: countryName, path: countryPath }] : []),
    ...visibleSegments.map((item, visibleIndex) => {
      const isLast = !hasQueryCrumb && visibleIndex === visibleSegments.length - 1;
      const label = resolveBreadcrumbSegmentLabel(item.segment, {
        isPropertyPage,
        overrideLabel: item.overrideLabel,
        country: params?.country,
      });

      return {
        name: label,
        path: isLast ? null : item.path,
      };
    }),
    ...(hasQueryCrumb ? [{ name: queryLabel, path: null }] : []),
  ];
  const baseDomain = GetDomainName();
  // Use the clean canonical path (no query string) for the current-page breadcrumb item
  // so the JSON-LD @id matches the canonical URL and stays stable across filter/sort params.
  const currentPageUrl = hasQueryCrumb
    ? `${baseDomain}${location.pathname}${location.search}`
    : `${baseDomain}${location.pathname}`;
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbData, currentPageUrl, baseDomain);

  return (
    <>
      {breadcrumbSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      )}
      <Breadcrumb className={className}>
        <BreadcrumbList
          key={location.pathname}
          className='text-sm text-muted-foreground'
        >
          <BreadcrumbItem>
            <BreadcrumbLink data-spinner="true" href={homePath}>Home</BreadcrumbLink>
          </BreadcrumbItem>

          {countryRequired && (<>
            <BreadcrumbSeparator />

            <BreadcrumbItem>
              <BreadcrumbLink data-spinner="true" href={countryPath}>
                {countryName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </>
          )}
          {visibleSegments.map((item, visibleIndex) => {
            const isLast = !hasQueryCrumb && visibleIndex === visibleSegments.length - 1;
            const label = resolveBreadcrumbSegmentLabel(item.segment, {
              isPropertyPage,
              overrideLabel: item.overrideLabel,
              country: params?.country,
            });

            return (
              <React.Fragment key={`${item.path}:${visibleIndex}`}>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  {isLast ? (
                    <BreadcrumbPage className='capitalize text-sm text-muted-foreground'>
                      <Link data-spinner="true" to={item.path}>{label}</Link>
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link data-spinner="true" to={item.path} className='capitalize text-sm text-muted-foreground'>
                        {label}
                      </Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </React.Fragment>
            );
          })}
          {hasQueryCrumb ? (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className='capitalize text-sm text-muted-foreground'>
                  <Link
                    data-spinner="true"
                    to={`${location.pathname}${location.search}`}
                  >
                    {queryLabel}
                  </Link>
                </BreadcrumbPage>
              </BreadcrumbItem>
            </>
          ) : null}
        </BreadcrumbList>
      </Breadcrumb>
    </>
  );
}
